import {
    ApolloClient,
    HttpLink,
    from,
    InMemoryCache,
    NormalizedCacheObject,
    ApolloQueryResult,
    OperationVariables,
    QueryOptions,
    MutationOptions,
    FetchResult,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { NullOr } from "@regal-voice/shared-types";
import pick from "lodash/pick";

import { TAB_ID, TAB_ID_HEADER } from "App/main/Utils";
import { getCsrfToken, CSRF_TOKEN_HEADER } from "Services/CsrfTokenService";
import { typeDefs } from "Services/marketing-api/typedefs";
import { getApiUrl } from "Services/ProxyConfig";
import { fetch } from "Services/util/fetchPonyfill";

const customizeCachedFields = {
    typePolicies: {
        User: {
            fields: {
                reservations: {
                    merge(existing: any, incoming: any) {
                        // This is the current default behavior when no merge function is defined
                        // Defining a merge function protects the code from changes to default behavior and silences the warning
                        return incoming;
                    },
                },
            },
        },
        JourneyVersion: {
            keyFields: ["id", "version"],
        },
        // Versioned entities: Use composite keys to prevent Apollo from treating
        // different versions of the same entity as the same cached object.
        // Without this, all versions would share the same cache entry (keyed by id only),
        // causing only the last version to be stored.
        RoutingRule: {
            keyFields: ["id", "version"],
        },
        // Note: AiAgentVersion doesn't include 'id' in its query, so it doesn't need this fix
        // If it's added to the query in the future, add: AiAgentVersion: { keyFields: ["id", "version"] }
    },
};

// set a context on your operation, which is used by other links further down the chain
// add api headers in context
const authLink = setContext(async (_, { headers }) => {
    const csrfToken = getCsrfToken();
    return {
        headers: {
            ...headers,
            ...(csrfToken ? { [CSRF_TOKEN_HEADER]: csrfToken } : {}),
            [TAB_ID_HEADER]: TAB_ID,
        },
    };
});

let apolloClient: NullOr<ApolloClient<NormalizedCacheObject>> = null;

export function createGraphQLClient(): ApolloClient<NormalizedCacheObject> {
    apolloClient = new ApolloClient({
        link: from([
            authLink,
            new ErrorLink(({ graphQLErrors, networkError, response }) => {
                const { requestId } = (response as any) || {};
                if (requestId) {
                    if (graphQLErrors) {
                        (graphQLErrors as any).requestId = requestId;
                    } else if (networkError) {
                        (networkError as any).requestId = requestId;
                    }
                }
            }),
            new HttpLink({
                uri: `${getApiUrl()}/graphql`,
                credentials: "same-origin",
                fetch: (url, options) =>
                    fetch(url, options)
                        .then(
                            async (res) =>
                                new Response(
                                    JSON.stringify({
                                        ...(await res.json()),
                                        requestId: res.headers.get("X-RV-Request-ID"),
                                    }),
                                    pick(res, "ok", "status", "statusText", "headers")
                                )
                        )
                        .catch((err) => {
                            if ("name" in err && err.name === "AbortError") {
                                // prevent throwing abort errors because they're expected and harmless and should not be reported anywhere
                                return new Response(null, {
                                    status: 20, // this is the status code we're seeing for aborted requests
                                });
                            }
                            throw err; // let it run as before
                        }),
            }),
        ]),
        cache: new InMemoryCache(customizeCachedFields),
        typeDefs,
    });
    return apolloClient;
}

export function getApolloClient(): ApolloClient<NormalizedCacheObject> {
    if (apolloClient) {
        return apolloClient;
    }
    return createGraphQLClient();
}

export async function apolloQuery<T = any, TVariables extends OperationVariables = OperationVariables>(
    options: QueryOptions<TVariables, T>
): Promise<ApolloQueryResult<T>> {
    const client = getApolloClient();
    return client.query(options);
}

export function apolloMutation<T = any, TVariables extends OperationVariables = OperationVariables>(
    options: MutationOptions<T, TVariables>
): Promise<FetchResult<T>> {
    const client = getApolloClient();
    return client.mutate(options);
}
